/**
 * 🔐 用户菜单组件 | User Menu Component
 * 
 * 显示当前用户信息和角色
 */

import React from 'react';
import { Box, Chip, Typography, IconButton, Menu, MenuItem, Avatar } from '@mui/material';
import { AccountCircle, Logout } from '@mui/icons-material';
import { useAuth } from '../hooks/useAuth';
import { ROLE_LABELS, ROLE_COLORS } from '../hooks/usePermission';

export function UserMenu() {
  const { user, role, signOut } = useAuth();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  
  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  
  const handleClose = () => {
    setAnchorEl(null);
  };
  
  const handleSignOut = async () => {
    handleClose();
    
    // 强力清除函数：清除所有Clerk相关状态
    const forceCleanup = () => {
      // 1. 清除所有Cookie
      document.cookie.split(';').forEach(cookie => {
        const name = cookie.split('=')[0].trim();
        if (name.includes('clerk') || name.includes('__session') || name.includes('__client')) {
          document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
        }
      });
      
      // 2. 清除LocalStorage
      Object.keys(localStorage).forEach(key => {
        if (key.includes('clerk') || key.includes('@@auth')) {
          localStorage.removeItem(key);
        }
      });
      
      // 3. 清除SessionStorage
      sessionStorage.clear();
    };
    
    try {
      await signOut();
      forceCleanup();
      // 使用完整URL重新加载，确保状态清除
      window.location.href = window.location.origin + '/#/sign-in';
    } catch (error) {
      console.error('Sign out error:', error);
      forceCleanup();
      window.location.href = window.location.origin + '/#/sign-in';
    }
  };
  
  if (!user || !role) {
    return null;
  }
  
  return (
    <Box display="flex" alignItems="center" gap={1}>
      {/* 角色标签 */}
      <Chip 
        label={ROLE_LABELS[role]} 
        color={ROLE_COLORS[role]} 
        size="small"
        sx={{ fontWeight: 600 }}
      />
      
      {/* 用户名 */}
      <Typography variant="body2" sx={{ mr: 1, display: { xs: 'none', sm: 'block' } }}>
        {user.name}
      </Typography>
      
      {/* 用户菜单按钮 */}
      <IconButton
        onClick={handleMenu}
        color="inherit"
        aria-label="user menu"
      >
        <AccountCircle />
      </IconButton>
      
      {/* 下拉菜单 */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        {/* 用户信息 */}
        <Box sx={{ px: 2, py: 1, minWidth: 200 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            {user.name}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {user.email}
          </Typography>
          <Box mt={0.5}>
            <Chip 
              label={ROLE_LABELS[role]} 
              color={ROLE_COLORS[role]} 
              size="small"
            />
          </Box>
        </Box>
        
        {/* 登出 */}
        <MenuItem onClick={handleSignOut}>
          <Logout fontSize="small" sx={{ mr: 1 }} />
          退出登录 | Sign Out
        </MenuItem>
      </Menu>
    </Box>
  );
}

/**
 * 🎯 简单用户信息显示（不带菜单）
 */
export function UserInfo() {
  const { user, role } = useAuth();
  
  if (!user || !role) {
    return null;
  }
  
  return (
    <Box display="flex" alignItems="center" gap={1}>
      <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>
        {user.name.charAt(0).toUpperCase()}
      </Avatar>
      <Box>
        <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
          {user.name}
        </Typography>
        <Chip 
          label={ROLE_LABELS[role]} 
          color={ROLE_COLORS[role]} 
          size="small"
          sx={{ height: 16, fontSize: '0.7rem', mt: 0.25 }}
        />
      </Box>
    </Box>
  );
}

